/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import { getColor } from '@panel/commons/core/themev3';
import { useSnackbar, SNACKBAR_TYPE } from '@panel/commons/lib/snackbar';
import { Icon, useBreakpoint } from '@panel/commons';
import { isMobileDevice } from '@panel/commons/helpers/device';
import {
  backendForDND,
  MAX_FILE_HEIGHT,
  MAX_FILE_WIDTH,
  MIN_FILE_HEIGHT,
  MIN_FILE_WIDTH,
  MAX_FILES,
  MAX_FILE_SIZE,
  MIN_FILE_SIZE,
  createPicture
} from './helpers';
import { ItemList } from './itemlist';
import {
  SubtitleHelper,
  SectionContainer,
  HelperBox,
  FilesContainer,
  DropzoneContainer,
  CollapseCheck,
  CollapseContainer,
  CollapseWrapper
} from './styles';
import { ModuleWrapper } from '../../../Wrappers/ModuleWrapper';
import {
  getPictures,
  addMultimedia,
  updateMultimedia,
  deleteMultimedia,
  moveMultimedia,
  MultimediaType,
  MultimediaCollection,
  addErrorMultimedia
} from '../../../../ducks/step2';
import { setProcessingMultimedia } from '../../../../ducks/step2/actions';

const dropZoneConfig = Object.freeze({
  accept: 'image/*',
  multiple: true
});

const keysErrors = Object.freeze({
  format: {
    title: 'publisher.step2.module1.picture.error.format.title',
    subtitle: 'publisher.step2.module1.picture.error.format.subtitle'
  },
  sizeMin: {
    title: 'publisher.step2.module1.picture.error.size.min.title',
    subtitle: 'publisher.step2.module1.picture.error.size.min.subtitle'
  },
  sizeMax: {
    title: 'publisher.step2.module1.picture.error.size.max.title',
    subtitle: 'publisher.step2.module1.picture.error.size.max.subtitle'
  },
  dimensionsMin: {
    title: 'publisher.step2.module1.picture.error.dimensions.min.title',
    subtitle: 'publisher.step2.module1.picture.error.dimensions.min.subtitle'
  },
  dimensionsMax: {
    title: 'publisher.step2.module1.picture.error.dimensions.max.title',
    subtitle: 'publisher.step2.module1.picture.error.dimensions.max.subtitle'
  },
  generic: {
    title: 'publisher.step2.module1.picture.error.generic',
    subtitle: null
  }
});

export const resolveImageDimensions = file => {
  return new Promise(resolve => {
    const imageReader = new FileReader();
    imageReader.onload = () => {
      // eslint-disable-next-line no-undef
      const image = new Image();
      image.onload = () =>
        resolve({
          file,
          dimensions: { width: image.width, height: image.height }
        });
      image.onerror = e => resolve({ file }, e);
      imageReader.onerror = e => resolve({ file }, e);
      image.src = imageReader.result;
    };
    imageReader.readAsDataURL(file);
  });
};

const ImageSection = () => {
  const [collapse, setCollapse] = useState({ isCollapsed: false, keepOpen: false });
  const { images } = useSelector(getPictures);
  const { setToggleSnack, setSnackBar } = useSnackbar();
  const [reachedBreakpointDesktop] = useBreakpoint(1280);
  const isMobile = isMobileDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    const n = isMobile ? 5 : 3;
    setCollapse({ ...collapse, isCollapsed: images.length > n && !collapse.keepOpen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, isMobile]);

  const fireSnack = text => {
    setSnackBar(text, SNACKBAR_TYPE.DANGER);
    setToggleSnack(10000);
  };

  const fileDispatcher = (files = []) => {
    const take = MAX_FILES - images.length;
    if (take > 0 && files.length < take) {
      files.forEach(file => {
        const picture = createPicture(file, MultimediaType.IMAGE);
        dispatch(addMultimedia(picture, MultimediaCollection.IMAGE));
      });
    } else if (take > 0 && files.length >= take) {
      const filesToAccept = files.slice(0, take);
      filesToAccept.forEach(file => {
        const picture = createPicture(file, MultimediaType.IMAGE);
        dispatch(addMultimedia(picture, MultimediaCollection.IMAGE));
      });
      if (files.length > take) {
        fireSnack(t('publisher.step2.module1.picture.validation.max'));
      }
    } else {
      fireSnack(t('publisher.step2.module1.picture.validation.max'));
    }
  };

  const handleImageErrors = (results = []) => {
    const files = [];

    results.forEach(result => {
      if (result && Object.prototype.hasOwnProperty.call(result, 'dimensions')) {
        if (result.dimensions.width > MAX_FILE_WIDTH || result.dimensions.height > MAX_FILE_HEIGHT) {
          result.file.error = keysErrors.dimensionsMax;
        }
        if (result.dimensions.width < MIN_FILE_WIDTH || result.dimensions.height < MIN_FILE_HEIGHT) {
          result.file.error = keysErrors.dimensionsMin;
        }
      }
      if (result && !result.file.error) {
        if (result.file.size > MAX_FILE_SIZE) {
          result.file.error = keysErrors.sizeMax;
        }
        if (result.file.size < MIN_FILE_SIZE) {
          result.file.error = keysErrors.sizeMin;
        }
      }
      files.push(result.file);
    });
    if (files.find(f => f.error)) {
      fireSnack(t('publisher.step2.module1.picture.validationErrorLoading'));
    }
    fileDispatcher(files);
  };

  const validateDimensions = async files => {
    const filesQueue = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < files.length; i++) {
      const { type } = files[i];
      if (type === 'image/jpg' || type === 'image/jpeg' || type === 'image/png') {
        // eslint-disable-next-line no-await-in-loop
        const result = await resolveImageDimensions(files[i]);
        filesQueue.push(result);
      } else {
        files[i].error = keysErrors.format;
        filesQueue.push({ file: files[i] });
      }
    }
    handleImageErrors(filesQueue);
  };

  const onDrop = acceptedFiles => {
    if (acceptedFiles.length > 0) {
      validateDimensions(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropZoneConfig,
    onDrop
  });

  const onDelete = id => {
    dispatch(deleteMultimedia(id, MultimediaCollection.IMAGE));
  };

  const onUpdate = multimedia => {
    dispatch(updateMultimedia(multimedia, MultimediaCollection.IMAGE));
  };

  const onMove = (dragIndex, hoverIndex) => {
    dispatch(moveMultimedia(dragIndex, hoverIndex, MultimediaCollection.IMAGE));
  };

  const onError = (id, keyError = keysErrors.generic) => {
    dispatch(addErrorMultimedia(id, MultimediaCollection.IMAGE, keyError));
    fireSnack(t('publisher.step2.module1.picture.validationErrorLoading'));
  };

  const getMaxHeight = () => {
    const minImages = isMobile ? 1 : 2;
    const divisor = isMobile ? 2 : 3;
    const res = images.length > minImages ? Math.ceil(images.length / divisor) * 245 + 245 : 250;
    return res;
  };

  const onStatusChange = useCallback(({ key, status }) => {
    if (key) {
      dispatch(setProcessingMultimedia({ key, status }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ModuleWrapper title={t('publisher.step2.module1.picture.title')}>
        <SubtitleHelper data-repa='step2-images-subtitle'>{t('publisher.step2.module1.picture.text')}</SubtitleHelper>
        <SectionContainer>
          <DndProvider backend={backendForDND}>
            <CollapseWrapper data-repa='step2-images-collapseWrapper' hasItems={images.length > 0}>
              <CollapseContainer
                data-repa='step2-images-collapseContainer'
                maxHeight={getMaxHeight()}
                isCollapsed={collapse.isCollapsed}
              >
                <FilesContainer data-repa='step2-images-filesContainer' isCollapsed={collapse.isCollapsed}>
                  <DropzoneContainer data-repa='step2-images-dropzone' {...getRootProps()} hasItems={images.length > 0}>
                    <input data-repa='step2-images-input' {...getInputProps()} />
                    {!isDragActive && (
                      <>
                        <Icon
                          name='upload'
                          width={30}
                          height={30}
                          viewBox='0 0 46 46'
                          colorFill={getColor('primary')}
                        />
                        <span data-repa='step2-images-spanText'>
                          {t(`publisher.step2.module1.picture.placeholder.${reachedBreakpointDesktop ? 'd' : 'm'}`)}
                        </span>
                      </>
                    )}
                  </DropzoneContainer>
                  <ItemList
                    data-repa='step2-images-itemlist'
                    items={images}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onMove={onMove}
                    onError={onError}
                    mainMarker
                    mainMarkerTitle={t('publisher.step2.module1.mainPicture.title')}
                    editablePlaceholder={t('publisher.step2.module1.picture.footerPlaceholder')}
                    hasMain
                    onMainImageChange={onMove}
                    onStatusChange={onStatusChange}
                    keysErrors={keysErrors}
                    setCollapse={setCollapse}
                    collapse={collapse}
                    photoType='picture'
                  />
                </FilesContainer>
              </CollapseContainer>
              {!collapse.isCollapsed && (
                <CollapseCheck
                  data-repa='step2-images-collapseCheck'
                  showCollapse={images.length > (isMobile ? 3 : 5)}
                  onClick={() => setCollapse({ isCollapsed: !collapse.isCollapsed, keepOpen: !collapse.keepOpen })}
                >
                  <div data-repa='step2-images-collapse-label'>{t('publisher.step2.module1.picture.btn.less')}</div>
                  <Icon
                    data-repa='step2-images-collapse-icon'
                    name='chevronUp'
                    width={20}
                    height={20}
                    colorFill={getColor('primary')}
                    viewBox='0 0 16 16'
                  />
                </CollapseCheck>
              )}
            </CollapseWrapper>
          </DndProvider>
          <HelperBox data-repa='step2-images-helperBox'>
            <Icon name='nightlight' width={40} height={45} viewBox='0 0 40 45' colorFill={getColor('primary')} />
            <div data-repa='step2-images-helperBox-label'>{t('publisher.step2.module1.helperbox')}</div>
          </HelperBox>
        </SectionContainer>
      </ModuleWrapper>
    </>
  );
};

export { ImageSection };
